"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Public guide: /guides/how-your-grocery-budget-works — the consumer
 * explanation of BiteBalance's money concepts (Budget Model v2, consumer
 * revision; ingredient reuse live-proven in J18). Six sections, one per
 * glossary term, each headed by the term's canonical name with the same ⓘ
 * control the settings use; then the first-shop effect, ingredient reuse,
 * one illustrative example whose amounts reconcile, and where the numbers
 * live in the app. Glossary = definitions, this guide = the connected
 * explanation, the architecture document = technical ownership.
 *
 * Same standalone-route pattern as WhyTheScaleLiesPage.tsx: no auth, no
 * personal context; copy lives in packages/i18n's guides.en.ts /
 * guides.de.ts under `guides.howYourGroceryBudgetWorks`.
 */

import { useEffect } from "react"
import { Link } from "react-router-dom"
import { GLOSSARY_LINKS, type GlossaryTermId } from "@bitebalance/i18n"
import { Button } from "@/components/ui/button"
import { InfoButton } from "@/components/ui/InfoButton"
import { LanguageToggle } from "@/components/layout/LanguageToggle"
import { useI18n } from "@/i18n"
import { PageEntrance, ReadingProgress } from "@/components/PageEntrance"

const K = "guides.howYourGroceryBudgetWorks"

/** The six money concepts, in reading order, each bound to its glossary term. */
const CONCEPTS: ReadonlyArray<{ key: string; termId: GlossaryTermId }> = [
  { key: "goal", termId: "weekly-spending-goal" },
  { key: "forecast", termId: "forecast-weekly-average" },
  { key: "nextShop", termId: "next-shop-estimate" },
  { key: "carryover", termId: "pantry-carryover" },
  { key: "maximum", termId: "maximum-checkout" },
]

export default function HowYourGroceryBudgetWorksPage() {
  const { t } = useI18n()

  useEffect(() => {
    const previousTitle = document.title
    document.title = t(`${K}.meta.title`)
    const meta = document.querySelector('meta[name="description"]')
    const previousDescription = meta?.getAttribute("content") ?? null
    meta?.setAttribute("content", t(`${K}.meta.description`))
    return () => {
      document.title = previousTitle
      if (previousDescription !== null) meta?.setAttribute("content", previousDescription)
    }
  }, [t])

  const exampleRows = [
    { label: t(`${K}.example.goalLabel`), value: t(`${K}.example.goalValue`), termId: "weekly-spending-goal" as const },
    { label: t(`${K}.example.nextShopLabel`), value: t(`${K}.example.nextShopValue`), termId: "next-shop-estimate" as const },
    { label: t(`${K}.example.consumedLabel`), value: t(`${K}.example.consumedValue`), termId: "food-consumed-this-week" as const },
    { label: t(`${K}.example.carryoverLabel`), value: t(`${K}.example.carryoverValue`), termId: "pantry-carryover" as const },
  ]

  return (
    <PageEntrance>
      <ReadingProgress />
      <div className="min-h-screen bg-cream text-ink">
        <a
          href="#main"
          className="sr-only focus-visible:not-sr-only focus-visible:fixed focus-visible:top-2 focus-visible:left-2 focus-visible:z-50 focus-visible:rounded-bb-md focus-visible:bg-paper focus-visible:px-4 focus-visible:py-2 focus-visible:text-ink focus-visible:shadow-[var(--bb-shadow)]"
        >
          {t("nav.skipToContent")}
        </a>

        <header className="border-b border-line bg-paper">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4 md:px-8">
            <Link to="/" className="text-sm font-semibold text-ink-2 hover:text-ink">
              ← {t(`${K}.backToHome`)}
            </Link>
            <LanguageToggle />
          </div>
        </header>

        <main id="main">
          <article className="mx-auto max-w-3xl px-4 py-14 md:px-8 md:py-20">
            <p className="text-sm font-semibold tracking-wide text-green uppercase">{t(`${K}.eyebrow`)}</p>
            <h1 className="mt-3 text-4xl leading-tight font-black text-ink md:text-5xl">{t(`${K}.hero.title`)}</h1>
            <p className="mt-5 max-w-prose text-lg text-ink-2">{t(`${K}.hero.subtitle`)}</p>
            <p className="mt-3 text-sm font-medium text-ink-2">{t(`${K}.hero.readTime`)}</p>

            {CONCEPTS.map(concept => (
              <section key={concept.key} className="mt-12" data-testid={`concept-${concept.termId}`}>
                <h2 className="flex items-center gap-2 text-2xl font-bold text-ink md:text-3xl">
                  <span>{t(`${K}.${concept.key}.title`)}</span>
                  <InfoButton termId={concept.termId} />
                </h2>
                <p className="mt-4 text-base leading-relaxed text-ink-2">{t(`${K}.${concept.key}.body1`)}</p>
                <p className="mt-4 text-base leading-relaxed text-ink-2">{t(`${K}.${concept.key}.body2`)}</p>
              </section>
            ))}

            <section className="mt-14 rounded-bb-xl border border-line bg-paper p-6 shadow-[var(--bb-shadow)] md:p-8">
              <h2 className="text-2xl font-bold text-ink md:text-3xl">{t(`${K}.firstShop.title`)}</h2>
              <p className="mt-4 text-base leading-relaxed text-ink-2">{t(`${K}.firstShop.body1`)}</p>
              <p className="mt-4 text-base leading-relaxed text-ink-2">{t(`${K}.firstShop.body2`)}</p>
            </section>

            <section className="mt-14">
              <h2 className="text-2xl font-bold text-ink md:text-3xl">{t(`${K}.reuse.title`)}</h2>
              <p className="mt-4 text-base leading-relaxed text-ink-2">{t(`${K}.reuse.body1`)}</p>
              <p className="mt-4 text-base leading-relaxed text-ink-2">{t(`${K}.reuse.body2`)}</p>
            </section>

            <section className="mt-14 rounded-bb-xl border border-line bg-paper p-6 shadow-[var(--bb-shadow)] md:p-8">
              <h2 className="text-2xl font-bold text-ink md:text-3xl">{t(`${K}.example.title`)}</h2>
              <p className="mt-2 text-sm font-medium text-ink-2">{t(`${K}.example.label`)}</p>
              <dl className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="budget-guide-example">
                {exampleRows.map(row => (
                  <div key={row.termId} className="rounded-bb-lg border border-line bg-cream p-4">
                    <dt className="text-sm text-ink-2">{row.label}</dt>
                    {/* The ⓘ sits AFTER the amount: its dialog opens below, so
                        the definition never covers the number it explains
                        (owner feedback 2026-09-05). */}
                    <dd className="mt-1 flex items-center gap-2 text-2xl font-bold text-ink">
                      <span data-testid={`example-${row.termId}`}>{row.value}</span>
                      <InfoButton termId={row.termId} />
                    </dd>
                  </div>
                ))}
              </dl>
              <p className="mt-6 text-base leading-relaxed text-ink-2">{t(`${K}.example.body1`)}</p>
              <p className="mt-4 text-base leading-relaxed text-ink-2">{t(`${K}.example.body2`)}</p>
            </section>

            <section className="mt-14">
              <h2 className="text-2xl font-bold text-ink md:text-3xl">{t(`${K}.whereToFind.title`)}</h2>
              <ul className="mt-4 flex flex-col gap-2">
                {[t(`${K}.whereToFind.item1`), t(`${K}.whereToFind.item2`), t(`${K}.whereToFind.item3`)].map(item => (
                  <li key={item} className="flex gap-3 text-base text-ink-2">
                    <span aria-hidden="true" className="mt-1 shrink-0 text-green">
                      ✓
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-ink-2">
                {t(`${K}.whereToFind.glossaryLabel`)}:{" "}
                <Link
                  to={GLOSSARY_LINKS.webPath}
                  className="font-semibold text-green underline underline-offset-2"
                  data-testid="budget-guide-glossary-link"
                >
                  {t(`${K}.whereToFind.glossaryLinkText`)}
                </Link>
              </p>
            </section>

            <section className="mt-14 rounded-bb-xl bg-green p-6 text-paper shadow-[var(--bb-shadow)] md:p-8">
              <h2 className="text-2xl font-bold md:text-3xl">{t(`${K}.cta.title`)}</h2>
              <p className="mt-3 text-base opacity-90">{t(`${K}.cta.body`)}</p>
              <Button asChild className="mt-6 bg-paper text-green hover:bg-cream">
                <Link to="/app">{t(`${K}.cta.button`)}</Link>
              </Button>
            </section>

            <p className="mt-10 text-xs leading-relaxed text-ink-2">{t(`${K}.disclaimer`)}</p>
          </article>
        </main>
      </div>
    </PageEntrance>
  )
}
